import { type RefObject, useCallback, useEffect, useRef } from "react";

const NEAR_BOTTOM_TOLERANCE = 120;
const STICK_DURATION_MS = 1500;

const isNearBottom = (element: HTMLElement) =>
	element.scrollHeight - element.scrollTop - element.clientHeight <=
	NEAR_BOTTOM_TOLERANCE;

const scrollToBottom = (element: HTMLElement) => {
	element.scrollTop = element.scrollHeight;
};

export const useChatScrollAnchor = (
	scrollRef: RefObject<HTMLDivElement | null>,
) => {
	const stickToBottomRef = useRef(true);
	const forcedUntilRef = useRef(0);

	useEffect(() => {
		const scroller = scrollRef.current;
		if (!scroller) return;

		let lastScrollHeight = scroller.scrollHeight;
		let frame: number;

		const shouldStick = () =>
			stickToBottomRef.current || performance.now() < forcedUntilRef.current;

		const handleScroll = () => {
			if (performance.now() < forcedUntilRef.current) return;
			stickToBottomRef.current = isNearBottom(scroller);
		};

		const watchGrowth = () => {
			frame = requestAnimationFrame(watchGrowth);

			if (scroller.scrollHeight === lastScrollHeight) return;
			lastScrollHeight = scroller.scrollHeight;

			if (shouldStick()) {
				scrollToBottom(scroller);
			}
		};

		const resizeObserver = new ResizeObserver(() => {
			if (shouldStick()) {
				scrollToBottom(scroller);
			}
		});

		scroller.addEventListener("scroll", handleScroll, { passive: true });
		resizeObserver.observe(scroller);
		frame = requestAnimationFrame(watchGrowth);

		return () => {
			cancelAnimationFrame(frame);
			resizeObserver.disconnect();
			scroller.removeEventListener("scroll", handleScroll);
		};
	}, [scrollRef]);

	return useCallback(() => {
		const scroller = scrollRef.current;
		if (!scroller) return;

		stickToBottomRef.current = true;
		forcedUntilRef.current = performance.now() + STICK_DURATION_MS;
		scrollToBottom(scroller);
	}, [scrollRef]);
};
